import { useId, useMemo } from "react";
import type { CoreCharacter } from "../../shared/refinement";
import type { ArtView, BambooStyle } from "../types";

interface CharacterTemplate {
  seed: number;
  bend: number;
  spread: number;
  density: number;
}

interface BranchSpec {
  start: [number, number];
  control: [number, number];
  end: [number, number];
}

const NATURAL_BRANCHES: BranchSpec[] = [
  { start: [128, 407], control: [214, 370], end: [298, 292] },
  { start: [132, 330], control: [205, 266], end: [272, 162] },
  { start: [137, 244], control: [88, 199], end: [70, 130] },
  { start: [140, 184], control: [205, 145], end: [260, 82] },
  { start: [130, 365], control: [82, 348], end: [42, 302] },
];

const CHARACTER_TEMPLATES: Record<CoreCharacter, CharacterTemplate> = {
  心: { seed: 11, bend: -12, spread: 1.05, density: 42 },
  守: { seed: 17, bend: -7, spread: 0.92, density: 44 },
  信: { seed: 23, bend: -3, spread: 1.08, density: 46 },
  义: { seed: 29, bend: 8, spread: 1.12, density: 41 },
  勇: { seed: 31, bend: -10, spread: 0.94, density: 47 },
  恩: { seed: 37, bend: 4, spread: 1.03, density: 45 },
  志: { seed: 41, bend: -5, spread: 0.9, density: 43 },
  真: { seed: 43, bend: 2, spread: 1.15, density: 48 },
  和: { seed: 47, bend: -8, spread: 1.0, density: 40 },
  别: { seed: 53, bend: 11, spread: 1.09, density: 39 },
  念: { seed: 59, bend: -6, spread: 0.96, density: 44 },
  归: { seed: 61, bend: 6, spread: 1.04, density: 42 },
  行: { seed: 67, bend: -13, spread: 1.18, density: 40 },
  静: { seed: 71, bend: 0, spread: 0.88, density: 49 },
  诚: { seed: 73, bend: -4, spread: 1.01, density: 46 },
  诺: { seed: 79, bend: 5, spread: 1.07, density: 47 },
  舍: { seed: 83, bend: 9, spread: 1.13, density: 38 },
  起: { seed: 89, bend: -14, spread: 1.2, density: 41 },
  安: { seed: 97, bend: 1, spread: 0.89, density: 45 },
  愿: { seed: 101, bend: -2, spread: 0.98, density: 48 },
  清: { seed: 103, bend: 7, spread: 1.05, density: 43 },
  明: { seed: 107, bend: -9, spread: 1.11, density: 42 },
  久: { seed: 109, bend: 12, spread: 1.16, density: 37 },
  新: { seed: 113, bend: -11, spread: 1.19, density: 46 },
};

interface BambooArtProps {
  character?: string;
  phrase?: string;
  view?: ArtView;
  style?: BambooStyle;
  className?: string;
  labelled?: boolean;
}

function templateForCharacter(character: string): CharacterTemplate {
  const approved = CHARACTER_TEMPLATES[character as CoreCharacter];
  if (approved) return approved;
  const seed = Array.from(character).reduce((sum, item) => sum + (item.codePointAt(0) ?? 0), 0) || 127;
  return {
    seed,
    bend: (seed % 25) - 12,
    spread: 0.9 + (seed % 30) / 100,
    density: 40 + (seed % 9),
  };
}

function random(seed: number) {
  let value = seed;
  return () => {
    value = (value * 9301 + 49297) % 233280;
    return value / 233280;
  };
}

function quadraticPoint(start: number, control: number, end: number, t: number): number {
  const inverse = 1 - t;
  return inverse * inverse * start + 2 * inverse * t * control + t * t * end;
}

function quadraticTangent(start: number, control: number, end: number, t: number): number {
  return 2 * (1 - t) * (control - start) + 2 * t * (end - control);
}

export function BambooArt({
  character = "信",
  phrase = "守心不改",
  view = "painting",
  style = "wind",
  className = "",
  labelled = true,
}: BambooArtProps) {
  const rawId = useId();
  const clipId = `character-${rawId.replace(/:/g, "")}`;
  const template = templateForCharacter(character);
  const leaves = useMemo(() => {
    const next = random(template.seed + style.length * 19);
    const samplePositions =
      style === "dense"
        ? [0.28, 0.45, 0.62, 0.78, 0.92]
        : style === "sparse"
          ? [0.52, 0.76, 0.92]
          : [0.38, 0.58, 0.76, 0.91];
    return NATURAL_BRANCHES.flatMap((branch, branchIndex) =>
      samplePositions.map((t, sampleIndex) => {
        const x = quadraticPoint(branch.start[0], branch.control[0], branch.end[0], t);
        const y = quadraticPoint(branch.start[1], branch.control[1], branch.end[1], t);
        const tangentX = quadraticTangent(branch.start[0], branch.control[0], branch.end[0], t);
        const tangentY = quadraticTangent(branch.start[1], branch.control[1], branch.end[1], t);
        const tangentAngle = (Math.atan2(tangentY, tangentX) * 180) / Math.PI;
        const extendsRight = branch.end[0] > branch.start[0];
        const flowAngle =
          style === "wind"
            ? extendsRight ? -22 : -158
            : style === "rain"
              ? extendsRight ? 62 : 118
              : tangentAngle;
        const sides =
          style === "dense"
            ? [-1, 0, 1]
            : style === "sparse"
              ? sampleIndex === samplePositions.length - 1 ? [-1, 1] : [sampleIndex % 2 ? 1 : -1]
              : sampleIndex % 2 === 0 ? [-1, 1] : [0, 1];
        return sides.map((side) => {
          const rotation = flowAngle + side * (style === "rain" ? 14 : 20) + (next() - 0.5) * 8;
          return {
            x: x + (next() - 0.5) * 4,
            y: y + (next() - 0.5) * 4,
            length: 30 + next() * 12 + (style === "dense" ? 2 : 0),
            width: 3.2 + next() * 1.7,
            rotate: rotation,
            twig: 7 + next() * 5,
            opacity: 0.7 + next() * 0.26,
            key: `${branchIndex}-${sampleIndex}-${side}`,
          };
        });
      }).flat(),
    );
  }, [character, style, template]);

  const label =
    view === "painting"
      ? `${style === "rain" ? "雨" : "风"}中竹画`
      : view === "character"
        ? `竹叶组成“${character}”字`
        : `“${character}”字与心句“${phrase}”`;

  return (
    <svg
      className={`bamboo-art ${className}`}
      viewBox="0 0 360 520"
      role={labelled ? "img" : undefined}
      aria-label={labelled ? label : undefined}
      aria-hidden={labelled ? undefined : true}
    >
      <defs>
        <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
          <text x="180" y="352" textAnchor="middle" className="character-clip">
            {character}
          </text>
        </clipPath>
        <linearGradient id={`${clipId}-ink`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#0f0f0e" />
          <stop offset="0.52" stopColor="#242321" />
          <stop offset="1" stopColor="#6d6a63" />
        </linearGradient>
        <filter id={`${clipId}-wash`} x="-12%" y="-12%" width="124%" height="124%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.018 0.065"
            numOctaves="2"
            seed={template.seed}
            result="paperNoise"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="paperNoise"
            scale="1.35"
            xChannelSelector="R"
            yChannelSelector="G"
            result="roughInk"
          />
          <feGaussianBlur in="roughInk" stdDeviation="0.12" />
        </filter>
      </defs>

      {view !== "character" && (
        <g className="natural-bamboo" filter={`url(#${clipId}-wash)`}>
          <path d={`M126 493 C ${128 + template.bend} 360, ${116 + template.bend} 210, 144 42`} />
          {NATURAL_BRANCHES.map((branch, index) => (
            <path
              key={`branch-${index}`}
              d={`M ${branch.start[0]} ${branch.start[1]} Q ${branch.control[0]} ${branch.control[1]} ${branch.end[0]} ${branch.end[1]}`}
            />
          ))}
          <line x1="126" y1="424" x2="134" y2="424" />
          <line x1="130" y1="342" x2="138" y2="342" />
          <line x1="135" y1="258" x2="143" y2="258" />
          {leaves.map((leaf) => (
            <g key={leaf.key} opacity={leaf.opacity} transform={`translate(${leaf.x} ${leaf.y}) rotate(${leaf.rotate})`}>
              <line className="leaf-twig" x1="0" y1="0" x2={leaf.twig} y2="0" />
              <path
                className="ink-leaf"
                d={`M ${leaf.twig} 0 C ${leaf.twig + leaf.length * 0.3} ${-leaf.width}, ${leaf.twig + leaf.length * 0.78} ${-leaf.width * 0.58}, ${leaf.twig + leaf.length} 0 C ${leaf.twig + leaf.length * 0.72} ${leaf.width * 0.72}, ${leaf.twig + leaf.length * 0.28} ${leaf.width * 0.86}, ${leaf.twig} 0 Z`}
              />
            </g>
          ))}
        </g>
      )}

      {view === "character" && (
        <g className="character-bamboo">
          <text x="180" y="352" textAnchor="middle" className="character-ghost">
            {character}
          </text>
          <g clipPath={`url(#${clipId})`} filter={`url(#${clipId}-wash)`}>
            {Array.from({ length: 156 }, (_, index) => {
              const x = 43 + (index % 12) * 25;
              const y = 62 + Math.floor(index / 12) * 27;
              const angle = index % 2 ? -24 : 28;
              return (
                <path
                  key={index}
                  className="ink-leaf"
                  d="M -16 0 Q 0 -6.4 16 0 Q 0 4.8 -16 0 Z"
                  transform={`translate(${x} ${y}) rotate(${angle})`}
                  fill={`url(#${clipId}-ink)`}
                />
              );
            })}
          </g>
        </g>
      )}

      {view === "meaning" && (
        <g className="meaning-mark">
          <circle cx="280" cy="90" r="39" />
          <text x="280" y="108" textAnchor="middle">
            {character}
          </text>
          <text x="34" y="474" className="meaning-phrase">
            {phrase}
          </text>
        </g>
      )}
    </svg>
  );
}
